import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PaymentOptions from "../../checkout/PaymentOptions"; // Reuse existing component
import { getAccessToken } from "../../../utils/authStorage";
import styles from "./VoucherCheckout.module.css";

// Use environment variable for the base URL
const ENV_API_URL =
    import.meta.env.VITE_API_URL || "https://pixelmoonstore.in/api/v1";
const BASE_API_URL = ENV_API_URL.replace(/\/v1\/?$/, "");

export default function VoucherCheckout() {
    const { state } = useLocation();
    const navigate = useNavigate();
    const voucher = state?.voucher;

    const [paymentMethod, setPaymentMethod] = useState("matrixsols");
    // For matrixsols, we need the specific method (qr vs upi)
    const [upiMethod, setUpiMethod] = useState("qr");
    const [loading, setLoading] = useState(false);
    const [user, setUser] = useState(null);
    const [deliveryEmail, setDeliveryEmail] = useState("");

    useEffect(() => {
        if (!voucher) {
            navigate("/vouchers");
            return;
        }
        // Fetch user profile to get email
        fetchUserProfile();
    }, [voucher, navigate]);

    const fetchUserProfile = async () => {
        try {
            const token = getAccessToken();
            if (!token) {
                navigate("/login", { state: { from: "/checkout/voucher" } });
                return;
            }

            const res = await fetch(`${BASE_API_URL}/v1/users/profile`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                const data = await res.json();
                setUser(data.user);
                // Pre-fill delivery email with user's email
                setDeliveryEmail(data.user.email);
            }
        } catch (error) {
            console.error("Failed to fetch user profile", error);
        }
    };

    const handlePaymentChange = (method, data = {}) => {
        setPaymentMethod(method);
        if (method === "matrixsols" && data.method) {
            setUpiMethod(data.method);
        }
    };

    const handlePlaceOrder = async () => {
        // Simple email validation
        if (!deliveryEmail || !deliveryEmail.includes("@")) {
            toast.error("Please enter a valid email address");
            return;
        }

        setLoading(true);
        try {
            const token = getAccessToken();
            const orderPayload = {
                gameId: voucher._id, // Hack: Pass voucher ID as gameId (backend handles this)
                packId: voucher._id,
                accountId: "VOUCHER_PURCHASE", // Placeholder required by backend
                quantity: 1,
                paymentMethod: paymentMethod,
                method: paymentMethod === "matrixsols" ? upiMethod : undefined,
                deliveryEmail: deliveryEmail,
            };

            const res = await fetch(`${BASE_API_URL}/v1/orders`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(orderPayload),
            });

            const data = await res.json();

            if (!res.ok) {
                throw new Error(data.message || "Failed to place order");
            }

            // Handle Redirects
            if (data.paymentInfo?.redirectUrl) {
                // Store success message in session/local storage or pass via URL for the next page
                sessionStorage.setItem("voucher_order_pending", "true");
                window.location.href = data.paymentInfo.redirectUrl;
            } else if (data.success) {
                toast.success("Order placed successfully!");
                navigate("/user-dashboard/orders", { state: { message: "Voucher code sent to your provided email" } });
            }

        } catch (error) {
            console.error(error);
            toast.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    if (!voucher) return null;

    return (
        <div className={styles.container}>
            <h1 className={styles.title}>Review & Pay</h1>

            <div className={styles.layout}>
                {/* Order Summary */}
                <div className={styles.summaryCard}>
                    <div className={styles.voucherPreview}>
                        {voucher.image && (
                            <img src={voucher.image} alt={voucher.name} className={styles.voucherImage} />
                        )}
                        <div>
                            <h2 className={styles.voucherName}>{voucher.name}</h2>
                            <span className={styles.badge}>{voucher.type} Voucher</span>
                        </div>
                    </div>

                    <div className={styles.divider} />

                    <div className={styles.row}>
                        <span>Price</span>
                        <span>₹{voucher.retailPrice}</span>
                    </div>
                    <div className={styles.row}>
                        <span>Quantity</span>
                        <span>1</span>
                    </div>
                    <div className={`${styles.row} ${styles.total}`}>
                        <span>Total</span>
                        <span>₹{voucher.retailPrice}</span>
                    </div>
                </div>

                {/* Payment Section */}
                <div className={styles.paymentSection}>
                    <h3 className={styles.sectionTitle}>Delivery Details</h3>
                    <div className={styles.emailBox}>
                        <label className={styles.label}>Delivery Email:</label>
                        <input
                            type="email"
                            className={styles.emailInput}
                            value={deliveryEmail}
                            onChange={(e) => setDeliveryEmail(e.target.value)}
                            placeholder="Enter email to receive code"
                        />
                        <p className={styles.hint}>The voucher code will be sent to this email address.</p>
                    </div>

                    <h3 className={styles.sectionTitle}>Select Payment Method</h3>
                    <div className={styles.paymentOptionsWrapper}>
                        <PaymentOptions
                            selectedMethod={paymentMethod}
                            onPaymentMethodChange={handlePaymentChange}
                            onVoucherApply={() => { }} // Disable voucher application for buying vouchers
                        />
                    </div>

                    <button
                        className={styles.payButton}
                        onClick={handlePlaceOrder}
                        disabled={loading}
                    >
                        {loading ? "Processing..." : `Pay ₹${voucher.retailPrice}`}
                    </button>
                </div>
            </div>
        </div>
    );
}
